import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';

export default function EmailConfirmation() {
  const { user, checkEmailConfirmation, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  
  // Get email from location state
  const email = location.state?.email || user?.email;

  useEffect(() => {
    const verifyEmail = async () => {
      if (checking || authLoading) return;
      
      try {
        setChecking(true);
        const { success, isConfirmed, error } = await checkEmailConfirmation();
        
        if (!success) {
          throw new Error(error || 'Failed to check email confirmation status');
        }

        if (isConfirmed) {
          // Email is confirmed, redirect to onboarding
          navigate('/onboarding', { replace: true });
        }
      } catch (err) {
        console.error('Email confirmation check error:', err);
        setError(err.message);
      } finally {
        setChecking(false);
      }
    };

    verifyEmail();
  }, [checking, authLoading, checkEmailConfirmation, navigate]);

  const handleResendEmail = async () => {
    try {
      setError(null);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email
      });
      
      if (error) throw error;
      
      // Show success message
      setError('Confirmation email sent! Please check your inbox.');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white p-4">
      <div className="w-full max-w-md text-center">
        <h1 className="text-2xl font-bold mb-4">Confirm Your Email</h1>
        
        <div className="bg-gray-800 rounded-lg p-6 mb-6">
          <p className="text-gray-300 mb-4">
            We've sent a confirmation email to:
            <br />
            <span className="font-medium text-white">{email}</span>
          </p>
          
          <p className="text-sm text-gray-400 mb-6">
            Please click the link in the email to verify your account.
            The confirmation link will expire in 24 hours.
          </p>

          {error && (
            <div className="p-3 mb-4 rounded bg-red-500/10 border border-red-500">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <button
            onClick={handleResendEmail}
            disabled={checking}
            className="text-primary hover:text-primary/90 text-sm underline"
          >
            Didn't receive the email? Click to resend
          </button>
        </div>

        <button
          onClick={() => navigate('/signin')}
          className="text-gray-400 hover:text-white text-sm"
        >
          ← Back to Sign In
        </button>
      </div>
    </div>
  );
} 